import cloneDeep from 'lodash.clonedeep';
import { Dispatch } from 'redux';

import { getInitialSearchLocation } from 'algolia-api/search/getInitialSearchLocation';
import { apiSetABExperimentBucket } from 'api/abExperiment/apiABExperiment';
import {
  ApiABExperimentBucketForId,
  ApiABExperimentId,
} from 'api/abExperiment/types/ApiABExperiment';
import {
  ApiApplicantProfileResponse,
  apiAddWorkHistoryJob,
  apiDeleteWorkHistoryJob,
  apiEditWorkHistoryJob,
  apiFetchApplicantProfile,
  apiUpdateApplicantProfile,
} from 'api/applicantProfile/apiApplicantProfile';
import { ApiApplicantProfile } from 'api/applicantProfile/types/ApiApplicantProfile';
import { ApiWorkHistoryJob } from 'api/applicantProfile/types/ApiWorkHistoryJob';
import {
  apiCreateSavedListing,
  apiDeleteSavedListing,
} from 'api/savedListing/apiSavedListing';
import {
  apiCreateSavedSearch as apiSaveSearch,
  apiDeleteSavedSearch as apiUnsaveSearch,
} from 'api/savedSearch/apiUserEnvironmentSavedSearch';
import {
  ApiSavedSearch,
  ApiSavedSearchUnsaved,
} from 'api/savedSearch/types/ApiSavedSearch';
import { fetchUserEnvironment as apiFetchUserEnvironment } from 'api/userEnvironment/apiUserEnvironment';
import {
  ApiEnvironment,
  ApiUser,
  ApiUserEnvironment,
} from 'api/userEnvironment/types/ApiUserEnvironment';
import { trackEvent } from 'utils/analytics/track/trackEvent';
import { removeId } from 'utils/functional';

import {
  MainStoreFlashMessagesAction,
  pushFlashMessage,
} from './flashMessages';
import { updateSearchLocation } from './mainSearch';

type FetchUserEnvironmentRequestAction = {
  type: 'USER_ENVIRONMENT_FETCH_REQUEST';
};

type FetchUserEnvironmentSuccessAction = {
  type: 'USER_ENVIRONMENT_FETCH_SUCCESS';
  userEnvironment: ApiUserEnvironment;
};

type UpdateUserEnvironmentUserAction = {
  type: 'UPDATE_USER_ENVIRONMENT_USER';
  user: ApiUser;
};

type UpdateUserEnvironmentEnvironmentAction = {
  type: 'UPDATE_USER_ENVIRONMENT_ENVIRONMENT';
  environment: ApiEnvironment;
};

type SaveListingAction = {
  type: 'SAVE_LISTING';
  listingId: string;
  listingType: string;
};

type UnsaveListingAction = {
  type: 'UNSAVE_LISTING';
  listingId: string;
};

type SaveSearchAction = {
  type: 'SAVE_SEARCH';
  savedSearch: ApiSavedSearch;
};

type UnsaveSearchAction = {
  type: 'UNSAVE_SEARCH';
  searchId: string;
};

type FetchApplicantRequestAction = {
  type: 'APPLICANT_PROFILE_FETCH_REQUEST';
};

type FetchApplicantSuccessAction = {
  type: 'APPLICANT_PROFILE_FETCH_SUCCESS';
  applicantProfileResponse: ApiApplicantProfileResponse;
};

type FetchApplicantFailureAction = {
  type: 'APPLICANT_PROFILE_FETCH_FAILURE';
};

export type MainStoreUserEnvironmentAction =
  | FetchUserEnvironmentRequestAction
  | FetchUserEnvironmentSuccessAction
  | UpdateUserEnvironmentUserAction
  | UpdateUserEnvironmentEnvironmentAction
  | SaveListingAction
  | UnsaveListingAction
  | SaveSearchAction
  | UnsaveSearchAction
  | FetchApplicantRequestAction
  | FetchApplicantSuccessAction
  | FetchApplicantFailureAction;

export type User = ApiUserEnvironment['user'] | undefined;

export type Environment = ApiUserEnvironment['environment'] | undefined;

export type MainStoreUserEnvironmentState = {
  environment: Environment;
  isEnvironmentFetching: boolean;
  isEnvironmentLoaded: boolean;
  user: User;
  isUserFetching: boolean;
  isUserLoaded: boolean;
  applicantProfile?: ApiApplicantProfile;
  isApplicantProfileLoaded: boolean;
  isApplicantProfileFetching: boolean;
};

export function userEnvironmentReducer(
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/default-param-last
  state: MainStoreUserEnvironmentState = {
    user: undefined,
    environment: undefined,
    applicantProfile: undefined,
    isEnvironmentFetching: false,
    isEnvironmentLoaded: false,
    isUserFetching: false,
    isUserLoaded: false,
    isApplicantProfileLoaded: false,
    isApplicantProfileFetching: false,
  },
  action: MainStoreUserEnvironmentAction,
): MainStoreUserEnvironmentState {
  switch (action.type) {
    case 'USER_ENVIRONMENT_FETCH_REQUEST':
      return { ...state, isEnvironmentFetching: true, isUserFetching: true };

    case 'USER_ENVIRONMENT_FETCH_SUCCESS':
      return {
        ...state,
        isEnvironmentFetching: false,
        isEnvironmentLoaded: true,
        isUserFetching: false,
        isUserLoaded: true,
        user: action.userEnvironment.user,
        environment: action.userEnvironment.environment,
      };

    case 'UPDATE_USER_ENVIRONMENT_USER':
      return {
        ...state,
        isUserFetching: false,
        isUserLoaded: true,
        user: action.user,
      };

    case 'UPDATE_USER_ENVIRONMENT_ENVIRONMENT':
      return {
        ...state,
        isEnvironmentFetching: false,
        isEnvironmentLoaded: true,
        environment: action.environment,
      };

    case 'SAVE_LISTING': {
      const { listingId, listingType } = action;

      if (
        // @ts-expect-error TS(2532): Object is possibly 'undefined'.
        state.user.savedListings.find((listing) => listing.id === listingId)
      ) {
        // already saved, no-op
        return state;
      }

      return {
        ...state,
        // @ts-expect-error TS(2322): Type '{ savedListings: { id: string; type: string;... Remove this comment to see the full error message
        user: {
          ...state.user,
          savedListings: [
            // @ts-expect-error TS(2532): Object is possibly 'undefined'.
            ...(state.user.savedListings || []),
            {
              id: listingId,
              type: listingType,
            },
          ],
        },
      };
    }

    case 'UNSAVE_LISTING': {
      const { listingId } = action;
      return {
        ...state,
        // @ts-expect-error TS(2322): Type '{ savedListings: { id: string; type: string;... Remove this comment to see the full error message
        user: {
          ...state.user,
          // @ts-expect-error TS(2532): Object is possibly 'undefined'.
          savedListings: removeId(state.user.savedListings, listingId),
        },
      };
    }

    case 'SAVE_SEARCH': {
      const { savedSearch } = action;

      if (
        // @ts-expect-error TS(2532): Object is possibly 'undefined'.
        state.user.savedSearches.find((search) => search.id === savedSearch.id)
      ) {
        // already saved, no-op
        return state;
      }

      return {
        ...state,
        // @ts-expect-error TS(2322): Type '{ savedSearches: ApiSavedSearch[]; id?: stri... Remove this comment to see the full error message
        user: {
          ...state.user,
          // @ts-expect-error TS(2532): Object is possibly 'undefined'.
          savedSearches: [...state.user.savedSearches, savedSearch],
        },
      };
    }

    case 'UNSAVE_SEARCH': {
      const { searchId } = action;
      return {
        ...state,
        // @ts-expect-error TS(2322): Type '{ savedSearches: ApiSavedSearch[]; id?: stri... Remove this comment to see the full error message
        user: {
          ...state.user,
          // @ts-expect-error TS(2532): Object is possibly 'undefined'.
          savedSearches: removeId(state.user.savedSearches, searchId),
        },
      };
    }

    case 'APPLICANT_PROFILE_FETCH_REQUEST':
      return { ...state, isApplicantProfileFetching: true };

    case 'APPLICANT_PROFILE_FETCH_SUCCESS':
      return {
        ...state,
        user: {
          ...state.user,
          // @ts-expect-error TS(2322): Type 'boolean | null' is not assignable to type 'b... Remove this comment to see the full error message
          isRecruitable: action.applicantProfileResponse.isRecruitable,
          resumeId: action.applicantProfileResponse.resumeId,
        },
        // @ts-expect-error TS(2322): Type 'ApplicantProfile | null' is not assignable t... Remove this comment to see the full error message
        applicantProfile: action.applicantProfileResponse.applicantProfile,
        isApplicantProfileFetching: false,
        isApplicantProfileLoaded: true,
      };

    case 'APPLICANT_PROFILE_FETCH_FAILURE':
      return { ...state, isApplicantProfileFetching: false };

    default:
      return state;
  }
}

const fetchUserEnvironmentRequest = (): FetchUserEnvironmentRequestAction => ({
  type: 'USER_ENVIRONMENT_FETCH_REQUEST',
});

const fetchUserEnvironmentSuccess = (
  userEnvironment: ApiUserEnvironment,
): FetchUserEnvironmentSuccessAction => ({
  type: 'USER_ENVIRONMENT_FETCH_SUCCESS',
  userEnvironment,
});

export const updateUserEnvironmentUser = (
  user: ApiUser,
): UpdateUserEnvironmentUserAction => ({
  type: 'UPDATE_USER_ENVIRONMENT_USER',
  user,
});

export const updateUserEnvironmentEnvironment = (
  environment: ApiEnvironment,
): UpdateUserEnvironmentEnvironmentAction => ({
  type: 'UPDATE_USER_ENVIRONMENT_ENVIRONMENT',
  environment,
});

export const fetchUserEnvironment =
  () =>
  (
    dispatch: Dispatch<MainStoreUserEnvironmentAction>,
    getState: () => { userEnvironment: MainStoreUserEnvironmentState },
  ): void => {
    const { userEnvironment } = getState();

    if (userEnvironment.isEnvironmentFetching) {
      return;
    }

    dispatch(fetchUserEnvironmentRequest());
    apiFetchUserEnvironment().then((fetchedUserEnvironment) => {
      // Read searchLocation from localstorage
      // @TODO replace fallback to searchLocation with guessedLocation
      // TODO: Fix this the next time the file is edited.

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      dispatch<any>(
        updateSearchLocation(
          getInitialSearchLocation(
            fetchedUserEnvironment.environment.guessedLocation,
          ),
          false,
        ),
      );
      dispatch(fetchUserEnvironmentSuccess(fetchedUserEnvironment));
    });
  };

export const updateUserEnvironment = fetchUserEnvironmentSuccess;

export const saveListing =
  (listingId: string, listingType: string) =>
  (
    dispatch: Dispatch<
      MainStoreUserEnvironmentAction | MainStoreFlashMessagesAction
    >,
  ) => {
    dispatch({
      type: 'SAVE_LISTING',
      listingId,
      listingType,
    });

    return apiCreateSavedListing(listingId, listingType).catch((error) => {
      dispatch({
        type: 'UNSAVE_LISTING',
        listingId,
        listingType,
      });
      throw error;
    });
  };

export const unsaveListing =
  (listingId: string, listingType: string) =>
  (
    dispatch: Dispatch<
      MainStoreUserEnvironmentAction | MainStoreFlashMessagesAction
    >,
  ) => {
    dispatch({
      type: 'UNSAVE_LISTING',
      listingId,
    });

    return apiDeleteSavedListing(listingId, listingType).catch((error) => {
      // if listing was already removed, ignore error
      if (error.statusCode !== 404) {
        dispatch({
          type: 'SAVE_LISTING',
          listingId,
          listingType,
        });
        throw error;
      }
    });
  };

export const saveSearch =
  (search: ApiSavedSearchUnsaved) =>
  (
    dispatch: Dispatch<
      MainStoreUserEnvironmentAction | MainStoreFlashMessagesAction
    >,
  ) =>
    apiSaveSearch(search).then((savedSearch) => {
      dispatch({
        type: 'SAVE_SEARCH',
        savedSearch,
      });
      dispatch(
        pushFlashMessage({
          type: 'SAVED_SEARCH',
        }),
      );
      trackEvent('Created Email Alert');
    });

export const unsaveSearch =
  (searchId: string) =>
  (
    dispatch: Dispatch<
      MainStoreUserEnvironmentAction | MainStoreFlashMessagesAction
    >,
  ) =>
    apiUnsaveSearch(searchId).then(() => {
      dispatch({
        type: 'UNSAVE_SEARCH',
        searchId,
      });
      dispatch(
        pushFlashMessage({
          type: 'UNSAVED_SEARCH',
        }),
      );
    });

export function setABExperimentBucket<TExperimentId extends ApiABExperimentId>(
  experimentId: TExperimentId,
  bucket?: ApiABExperimentBucketForId<TExperimentId>,
) {
  return (
    dispatch: Dispatch<MainStoreUserEnvironmentAction>,
    getState: () => { userEnvironment: MainStoreUserEnvironmentState },
  ) => {
    const userEnvironment = cloneDeep(getState().userEnvironment);
    return apiSetABExperimentBucket(experimentId, bucket).then(
      (abExperiments) => {
        // @ts-expect-error fix type errors
        userEnvironment.environment.abExperiments = abExperiments;
        // @ts-expect-error fix type errors
        dispatch(updateUserEnvironment(userEnvironment));
        return abExperiments;
      },
    );
  };
}

const dispatchProfileUpdate = (
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  request: (...args: Array<any>) => Promise<ApiApplicantProfileResponse>,
  dispatch: Dispatch<MainStoreUserEnvironmentAction>,
) => {
  dispatch({
    type: 'APPLICANT_PROFILE_FETCH_REQUEST',
  });
  return request()
    .then((applicantProfileResponse) => {
      dispatch({
        type: 'APPLICANT_PROFILE_FETCH_SUCCESS',
        applicantProfileResponse,
      });
    })
    .catch((error) => {
      dispatch({
        type: 'APPLICANT_PROFILE_FETCH_FAILURE',
      });
      throw error;
    });
};

export const fetchApplicantProfile =
  () => (dispatch: Dispatch<MainStoreUserEnvironmentAction>) =>
    dispatchProfileUpdate(() => apiFetchApplicantProfile(), dispatch);

export const updateApplicantProfile =
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (values: Record<string, any>) => (dispatch: Dispatch) =>
    dispatchProfileUpdate(() => apiUpdateApplicantProfile(values), dispatch);

export const updateWorkHistoryJob =
  (job: ApiWorkHistoryJob, id: string | null | undefined) =>
  (dispatch: Dispatch) => {
    const submit = () =>
      id ? apiEditWorkHistoryJob(id, job) : apiAddWorkHistoryJob(job);

    return dispatchProfileUpdate(() => submit(), dispatch);
  };

export const deleteWorkHistoryJob = (id: string) => (dispatch: Dispatch) =>
  dispatchProfileUpdate(() => apiDeleteWorkHistoryJob(id), dispatch);
